import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import agent from "../../api/agent";
import { SurveyDto } from "../../models/surveyDto";
import SurveyPlayer from "./surveyPlayer";
import { toast } from "react-toastify";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import { useStore } from "../../stores/store";
import { useTranslation } from "react-i18next";
import InitiateSurveyUz from "../PreSurveyPages/uz/initiateSurvey";
import InitiateSurveyKh from "../PreSurveyPages/kh/initiateSurvey";
import PostSurvey from "../PostSurveyPages/common/postSurvey";

interface Params {
  surveyCode: string;
}

export default function TakeSurvey() {
  let { surveyCode } = useParams<Params>();
  const { commonStore } = useStore();
  const { t } = useTranslation("common");
  const [loadingSurvey, setLoadingSurvey] = useState<boolean>(true);
  const [surveyInfo, setSurveyInfo] = useState<SurveyDto>();
  const [survey, setSurvey] = useState<SurveyDto>();
  const [filledSurvey, setFilledSurvey] = useState<SurveyDto>();
  const [startSurvey, setStartSurvey] = useState<boolean>(false);
  const [postSurvey, setPostSurvey] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const history = useHistory();
  const handleError = (error: any) => toast.error(t("Common.OfflineMessage"));

  useEffect(() => {
    //to prevent browser back button
    window.history.pushState(null, "", window.location.pathname);
    window.onpopstate = (e) => {
      window.history.pushState(null, "", window.location.pathname);
    };
  }, []);

  useEffect(() => {
    //Load basic survey info to ensure survey exists
    agent.Surveys.info(surveyCode)
      .then((result) => {
        commonStore.setSurveyCode(surveyCode);
        commonStore.setCurrentUserSurveyId(0);
        commonStore.setCurrentSurveyId(result.id);
        setSurveyInfo(result);
        loadSurvyeDetails();
        let language = commonStore.lang ? commonStore.lang : commonStore.defaultLang;
        agent.Translations.getSurveyContent(surveyCode, language).then((result) => {
          commonStore.setSurveyConentTranslations(result);
        });
      })
      .catch((error) => {
        toast.error("Invalid Survey Code");
        history.push("/");
      })
      .finally(() => {
        setLoadingSurvey(false);
      });
  }, []);

  const loadSurvyeDetails = () => {
    agent.Surveys.load(surveyCode)
      .then((result) => {
        setSurvey(result);
      })
      .catch((error) => {
        toast.error("Failed to load survey !");
      });
  };

  const playSurvey = () => {
    if (survey) setSurvey({ ...survey, userSurveyId: commonStore.currentUserSurveyId });
    setStartSurvey(true);
  };

  const submitSurvey = () => {
    if (!filledSurvey) return;
    setSubmitting(true);
    agent.UserSurveys.submit(filledSurvey)
      .then((result) => {
        toast.success(t("UserSurveyResult.SurveySubmittedSuccessfullyMessage"));
        history.push(`/results/${surveyCode}/${result}`);
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  let preSurveyControls: { [key: string]: any } = {};
  preSurveyControls["uzb"] = (
    <InitiateSurveyUz
      surveyCode={surveyCode}
      onStartSurvey={() => {
        playSurvey();
      }}
    />
  );

  preSurveyControls["kh"] = (
    <InitiateSurveyKh
      surveyCode={surveyCode}
      onStartSurvey={() => {
        playSurvey();
      }}
    />
  );

  preSurveyControls["kh2"] = (
    <InitiateSurveyKh
      surveyCode={surveyCode}
      onStartSurvey={() => {
        playSurvey();
      }}
    />
  );

  let postSurveyControls: { [key: string]: any } = {};
  postSurveyControls["uzb"] = (
    <PostSurvey
      userSurveyId={commonStore.currentUserSurveyId}
      onNavigatePrevious={() => {
        setPostSurvey(false);
        setStartSurvey(true);
      }}
      postQuestionsEnded={() => {
        setPostSurvey(false);
        submitSurvey();
      }}
    />
  );

  postSurveyControls["kh"] = (
    <PostSurvey
      userSurveyId={commonStore.currentUserSurveyId}
      onNavigatePrevious={() => {
        setPostSurvey(false);
        setStartSurvey(true);
      }}
      postQuestionsEnded={() => {
        setPostSurvey(false);
        submitSurvey();
      }}
    />
  );

  postSurveyControls["kh2"] = (
    <PostSurvey
      userSurveyId={commonStore.currentUserSurveyId}
      onNavigatePrevious={() => {
        setPostSurvey(false);
        setStartSurvey(true);
      }}
      postQuestionsEnded={() => {
        setPostSurvey(false);
        submitSurvey();
      }}
    />
  );

  const loadPreSurveyControl = () => {
    if (!surveyInfo) return;
    let code = surveyInfo.questionSet.toLowerCase();
    if (!preSurveyControls[code]) return <></>;
    return preSurveyControls[code];
  };

  const loadPostSurveyControl = () => {
    if (!surveyInfo) return;
    let code = surveyInfo.questionSet.toLowerCase();
    if (!postSurveyControls[code]) return <></>;
    return postSurveyControls[code];
  };

  const surveyLoader = (
    <Segment className="injaz-loader-tbl">
      <Dimmer active inverted>
        <Loader size="large">Loading Survey ...</Loader>
      </Dimmer>
    </Segment>
  );

  const submitLoader = (
    <Segment className="injaz-loader-tbl">
      <Dimmer active inverted>
        <Loader size="large">{t("UserSurveyResult.Submitting")} ...</Loader>
      </Dimmer>
    </Segment>
  );

  const content = (
    <>
      {!startSurvey && loadPreSurveyControl()}

      {survey && (
        <div style={{ display: startSurvey && !postSurvey ? "inline" : "none" }}>
          <SurveyPlayer
            survey={survey}
            userSurveyId={commonStore.currentUserSurveyId}
            onEndSurvey={(sur: SurveyDto) => {
              setFilledSurvey({ ...sur, userSurveyId: commonStore.currentUserSurveyId });
              setPostSurvey(true);
            }}
          />
        </div>
      )}

      {survey && (
        <div style={{ display: postSurvey ? "inline" : "none" }}>{loadPostSurveyControl()}</div>
      )}
    </>
  );

  return (
    <>
      {loadingSurvey && surveyLoader}
      {!loadingSurvey && submitting && submitLoader}
      {!loadingSurvey && !submitting && content}
    </>
  );
}
